import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  MessageCircle, 
  Zap, 
  Heart, 
  Shield, 
  TrendingDown,
  Clock,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const Home = () => {
  const whatsappNumber = '905078643838';
  
  const handleWhatsAppClick = () => {
    const message = 'Merhaba! Retatrutide hakkında bilgi almak istiyorum.';
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // Google Analytics Event Tracking
    if (window.gtag) {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: 'WhatsApp Button Click',
        value: 1
      });
    }
    
    window.location.href = url;
  };

  const benefits = [
    {
      icon: <TrendingDown className="w-10 h-10" />,
      title: 'Hızlı Kilo Kaybı',
      description: 'Klinik çalışmalarda kanıtlanmış etkin sonuçlar ile hedefinize ulaşın.'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Metabolizma Desteği',
      description: 'Vücudunuzun doğal yağ yakma sürecini optimize edin.'
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Güvenilir Formül',
      description: 'Bilimsel araştırmalarla desteklenen güvenli ve etkili içerik.'
    },
    {
      icon: <Zap className="w-10 h-10" />,
      title: 'Artan Enerji',
      description: 'Gün boyu süren enerji ve performans artışı yaşayın.'
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: 'Uzun Süreli Etki',
      description: 'Kalıcı sonuçlar için sürdürülebilir çözüm.'
    },
    {
      icon: <CheckCircle2 className="w-10 h-10" />,
      title: 'Kolay Kullanım',
      description: 'Pratik uygulama ile günlük hayatınıza kolayca entegre olur.'
    }
  ];

  const faqs = [
    {
      question: 'Retatrutide nedir ve neden farklıdır?',
      answer: 'Retatrutide, yeni nesil üçlü agonist bir kilo yönetimi ilacıdır. Ozempic\'teki semaglutide ve Mounjaro\'daki tirzepatide\'in aksine, sadece GLP-1 ve GIP değil, aynı zamanda glukagon reseptörlerini de aktive ederek 3 hormonal yolu birden hedef alır. Bu üçlü etki mekanizması, daha güçlü kilo kaybı sonuçları sağlar.'
    },
    {
      question: 'Retatrutide, Mounjaro ve Ozempic\'ten nasıl daha etkilidir?',
      answer: 'Klinik çalışmalar, retatrutide\'in Mounjaro\'daki tirzepatide (çift agonist) ve Ozempic\'teki semaglutide\'den (tek agonist) daha fazla kilo kaybı sağladığını göstermiştir. Retatrutide\'in üçlü mekanizması sayesinde, ortalama kilo kaybı %24\'e kadar ulaşabilirken, semaglutide yaklaşık %15, tirzepatide ise %20 civarında kalmaktadır.'
    },
    {
      question: 'Üçlü agonist mekanizma ne anlama gelir?',
      answer: 'Ozempic sadece GLP-1 reseptörünü hedefler (tek agonist). Mounjaro, GLP-1 ve GIP reseptörlerini hedefler (çift agonist). Retatrutide ise GLP-1, GIP ve glukagon reseptörlerinin üçünü birden aktive eder (üçlü agonist). Bu, iştah kontrolü, metabolizma hızlandırma, yağ yakımı ve enerji dengesinde maksimum etki anlamına gelir.'
    },
    {
      question: 'Ne kadar sürede sonuç alırım?',
      answer: 'Retatrutide\'in üçlü etki mekanizması sayesinde, kullanıcılar genellikle 4-6 hafta içinde belirgin sonuçlar görmeye başlar. Bu, Ozempic ve Mounjaro\'ya göre daha hızlı ve daha etkili bir kilo kaybı sürecidir. Klinik çalışmalarda 48 hafta sonunda ortalama %24 vücut ağırlığı kaybı kaydedilmiştir.'
    },
    {
      question: 'Yan etkileri var mı ve diğer ilaçlardan farkı nedir?',
      answer: 'Retatrutide, semaglutide ve tirzepatide ile benzer yan etki profiline sahiptir ancak üçlü mekanizması nedeniyle genellikle daha iyi tolere edilir. En yaygın yan etkiler hafif mide bulantısı ve iştah azalmasıdır. Detaylı bilgi ve kişiselleştirilmiş değerlendirme için lütfen bizimle iletişime geçin.'
    },
    {
      question: 'Nasıl sipariş verebilirim?',
      answer: 'WhatsApp üzerinden bizimle iletişime geçerek hemen sipariş verebilirsiniz. Uzman ekibimiz, retatrutide\'in sizin için uygun olup olmadığını değerlendirecek ve size en iyi kullanım protokolünü sunacaktır.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
            Retatrutide
          </h1>
          <Button 
            onClick={handleWhatsAppClick}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            İletişime Geç
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 opacity-60"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold">
                  Yeni Nesil Kilo Yönetimi
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-slate-900">
                Retatrutide Türkiye - Mounjaro ve Ozempic'ten Daha Etkili
                <span className="block mt-2 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Kilo Verme İlacı
                </span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                Retatrutide satın al - Mounjaro ve Ozempic'e alternatif yeni nesil kilo verme ilacı. 
                Üçlü agonist formülü ile %24'e kadar kilo kaybı. Bilimsel olarak kanıtlanmış, güvenli ve etkili sonuçlar.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-lg px-8 py-6 shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp'tan Sipariş Ver
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
              <img 
                src="https://images.unsplash.com/photo-1591384640699-9a85bd036da2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHx3ZWlnaHQlMjBsb3NzfGVufDB8fHx8MTc3MzY5ODk5M3ww&ixlib=rb-4.1.0&q=85"
                alt="Retatrutide kilo verme sonuçları - Mounjaro ve Ozempic alternatif kilo kaybı"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Neden Retatrutide?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Bilimsel araştırmalarla desteklenen, kanıtlanmış faydaları keşfedin
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className="border-2 border-slate-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6 text-emerald-600 group-hover:scale-110 transition-transform duration-300">
                    {benefit.icon}
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">
                    {benefit.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Retatrutide Nedir - Detailed SEO Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-slate-900 mb-6 text-center">
            Retatrutide Nedir? Nasıl Çalışır?
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              <strong>Retatrutide</strong>, kilo yönetimi için geliştirilmiş yeni nesil bir ilaçtır ve tek agonist Ozempic (semaglutide) 
              ile çift agonist Mounjaro (tirzepatide) gibi ilaçların ötesinde, üçlü agonist mekanizması ile çalışan ilk ilaçtır. 
              Bu özelliği sayesinde, retatrutide kilo verme ilacı kategorisinde çığır açan bir yenilik sunmaktadır.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Üçlü Agonist Mekanizma: GLP-1, GIP ve Glukagon
            </h3>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Retatrutide, üç farklı hormonal yolu aynı anda hedef alarak maksimum kilo kaybı sağlar:
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-3">•</span>
                <span className="text-slate-700">
                  <strong>GLP-1 (Glukagon-benzeri Peptit-1):</strong> İştahı azaltır ve tokluk hissi verir. 
                  Ozempic'in (semaglutide) çalışma prensibi sadece bu reseptöre dayanır.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-3">•</span>
                <span className="text-slate-700">
                  <strong>GIP (Glukoza Bağımlı İnsülinotropik Polipeptit):</strong> İnsülin salgısını artırır ve yağ metabolizmasını hızlandırır. 
                  Mounjaro (tirzepatide) GLP-1 ve GIP'i birlikte hedefler.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-emerald-600 font-bold mr-3">•</span>
                <span className="text-slate-700">
                  <strong>Glukagon Reseptörü:</strong> Enerji harcamasını artırır ve yağ yakımını hızlandırır. 
                  Bu üçüncü mekanizma sadece retatrutide'de bulunur ve onu diğerlerinden ayıran en önemli özelliktir.
                </span>
              </li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Klinik Çalışma Sonuçları: %24'e Kadar Kilo Kaybı
            </h3>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              48 haftalık klinik çalışmalarda, retatrutide kullanan katılımcılar ortalama <strong>%24 vücut ağırlığı kaybı</strong> yaşamıştır. 
              Bu sonuç, Ozempic (semaglutide) ile elde edilen yaklaşık %15 ve Mounjaro (tirzepatide) ile elde edilen %20 kilo kaybından 
              önemli ölçüde daha yüksektir.
            </p>

            <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 my-8 rounded-r-lg">
              <p className="text-slate-800 font-semibold mb-2">Karşılaştırmalı Etkinlik:</p>
              <ul className="space-y-2">
                <li className="text-slate-700">📊 Ozempic (Semaglutide): ~%15 kilo kaybı</li>
                <li className="text-slate-700">📊 Mounjaro (Tirzepatide): ~%20 kilo kaybı</li>
                <li className="text-slate-700 font-bold text-emerald-700">📊 Retatrutide: ~%24 kilo kaybı ✓</li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Kimler Retatrutide Kullanmalı?
            </h3>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Retatrutide satın al seçeneği, kilo problemi yaşayan yetişkinler için önerilmektedir. Özellikle Ozempic veya Mounjaro 
              ile yeterli sonuç alamayan, daha güçlü bir kilo verme ilacı arayan kişiler için ideal bir alternatiftir. 
              Ancak hamilelik, emzirme döneminde ve bazı sağlık durumlarında kullanımı önerilmez.
            </p>
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-lg px-8 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Retatrutide Satın Al - WhatsApp Sipariş
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-600"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">
            Dönüşümünüze Bugün Başlayın
          </h3>
          <p className="text-xl text-emerald-50 mb-10 max-w-2xl mx-auto">
            Uzman ekibimiz size özel çözümler sunmak için hazır. 
            Hemen WhatsApp'tan iletişime geçin!
          </p>
          <Button 
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-white text-emerald-600 hover:bg-emerald-50 font-bold text-lg px-10 py-7 shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-110"
          >
            <MessageCircle className="w-6 h-6 mr-3" />
            WhatsApp ile Hemen Başla
            <ArrowRight className="w-6 h-6 ml-3" />
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-slate-50" id="sss">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Sıkça Sorulan Sorular - Retatrutide Hakkında
            </h2>
            <p className="text-xl text-slate-600">
              Retatrutide, Mounjaro ve Ozempic karşılaştırması - Merak ettiklerinizin cevapları
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border-2 border-slate-200 rounded-xl px-6 hover:border-emerald-300 transition-colors"
              >
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-emerald-600 py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-6">Başka sorunuz mu var?</p>
            <Button 
              onClick={handleWhatsAppClick}
              variant="outline"
              className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white font-semibold px-8 py-6 text-lg transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp'tan Sorun
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900 text-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-2xl font-bold mb-4 bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                Retatrutide
              </h4>
              <p className="text-slate-400">
                Sağlıklı yaşam ve kilo yönetimi için yeni nesil çözüm.
              </p>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4 text-lg">İletişim</h5>
              <p className="text-slate-400">WhatsApp: {whatsappNumber}</p>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4 text-lg">Hızlı Erişim</h5>
              <Button 
                onClick={handleWhatsAppClick}
                className="bg-emerald-600 hover:bg-emerald-700 w-full"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp'tan Ulaş
              </Button>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
            <p>&copy; 2024 Retatrutide. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsAppClick}
        className="fixed bottom-8 right-8 bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4 rounded-full shadow-2xl hover:shadow-emerald-500/50 hover:scale-110 transition-all duration-300 z-50 group"
        aria-label="WhatsApp ile iletişime geç"
      >
        <MessageCircle className="w-8 h-8 group-hover:rotate-12 transition-transform" />
      </button>
    </div>
  );
};

export default Home;
